import { useGetUserSegmentsQuery } from "redux/apis";
import { useWebStoreContext } from "contexts";
import { Fragment, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useLinks } from "hooks/useLinks";

interface UserSegmentsGateProps {
	children: ReactNode;
}

/**
 * Blocks rendering of children until user segments are loaded when needed
 * - For segment-required routes: always load segments (even without authToken ready)
 * - For other routes: only load if authenticated
 *
 * To add a new route that requires segments:
 * 1. Add the route path to SEGMENT_REQUIRED_ROUTES below
 * 2. Done! UserSegmentsGate will handle loading automatically
 */
export const UserSegmentsGate = ({
	children
}: UserSegmentsGateProps): JSX.Element => {
	const { authToken, isAutoLoginIn, store, userTracker } = useWebStoreContext();
	const { pathname } = useLocation();
	const links = useLinks();
	const userId = userTracker?.id;

	// Routes that require user segments to be loaded
	const SEGMENT_REQUIRED_ROUTES = [links.elite_deals.path];

	const isSegmentRoute = SEGMENT_REQUIRED_ROUTES.includes(pathname);

	// Preload segments on any authenticated route (except while auto-login is in progress)
	// so features that depend on segment cache (e.g. nav visibility) can resolve globally.
	const shouldSkip = !authToken?.value || isAutoLoginIn || !userId;

	// Fetch segments and wait for completion
	const { data, isLoading } = useGetUserSegmentsQuery(userId, {
		skip: shouldSkip
	});

	// On protected routes, wait until store is initialized to avoid transient false negatives
	if (isSegmentRoute && !store) {
		return <Fragment />;
	}

	// On segment-required routes, block rendering until segments are actually ready
	if (
		isSegmentRoute &&
		authToken?.value &&
		!isAutoLoginIn &&
		(isLoading || !data)
	) {
		return <Fragment />;
	}

	return <Fragment>{children}</Fragment>;
};
